import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Social provider icons as SVG components
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

const SocialLoginButtons = ({ onSuccess, onError, mode = 'login' }) => {
  const defaultProviders = {
    google: { enabled: true, name: 'Google' },
    facebook: { enabled: true, name: 'Facebook' },
    apple: { enabled: true, name: 'Apple' }
  };
  const [providers, setProviders] = useState(defaultProviders);
  const [loading, setLoading] = useState({});

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await axios.get(`${API}/auth/social/config`);
        if (response.data?.providers) {
          setProviders(response.data.providers);
        }
      } catch (err) {
        console.warn('Using default social providers');
      }
    };
    fetchProviders();
  }, []);

  // Open URL in-app browser on native, or redirect on web
  const openAuthUrl = async (url) => {
    if (window.Capacitor?.isNativePlatform?.()) {
      try {
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url, presentationStyle: 'popover' });
      } catch (e) {
        // Fallback if Browser plugin fails
        window.location.href = url;
      }
    } else {
      window.location.href = url;
    }
  };

  // Handle Google login via Emergent Auth
  const handleGoogleLogin = () => {
    setLoading(prev => ({ ...prev, google: true }));
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    openAuthUrl(`https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`);
  };

  // Handle Facebook login
  const handleFacebookLogin = () => {
    if (!providers?.facebook?.enabled) return;
    setLoading(prev => ({ ...prev, facebook: true }));
    
    const redirectUri = window.location.origin + '/auth/callback/facebook';
    const scope = 'email,public_profile';
    const authUrl = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${providers.facebook.app_id}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}&response_type=code`;
    openAuthUrl(authUrl);
  };

  // Handle Apple Sign In (web only — buttons are hidden on iOS native)
  const handleAppleLogin = async () => {
    setLoading(prev => ({ ...prev, apple: true }));
    try {
      // Web-based Apple Sign-In via redirect.
      // Native iOS Sign-In is intentionally NOT used here — the social buttons
      // are hidden on iOS for App Store compliance (Guideline 2.1a / 4.8).
      initiateWebAppleSignIn();
    } catch (err) {
      console.error('Apple sign-in error:', err);
      if (onError) onError('Apple sign-in is temporarily unavailable. Please use another sign-in method or try again later.');
    } finally {
      setLoading(prev => ({ ...prev, apple: false }));
    }
  };

  const initiateWebAppleSignIn = () => {
    const clientId = 'us.nutrifitness.app';
    const redirectUri = encodeURIComponent(window.location.origin + '/auth/callback/apple');
    const scope = 'name email';
    const state = Math.random().toString(36).substring(7);
    sessionStorage.setItem('apple_auth_state', state);
    const url = `https://appleid.apple.com/auth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code id_token&scope=${scope}&response_mode=form_post&state=${state}`;
    openAuthUrl(url);
  };

  const buttonClass = (enabled) => 
    `w-full py-3 px-4 rounded-xl font-semibold text-sm flex items-center justify-center gap-3 transition-all ${
      enabled 
        ? 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-gray-300 hover:shadow-md text-gray-700 dark:text-gray-300 dark:text-gray-200' 
        : 'bg-gray-100 dark:bg-gray-700 border-2 border-gray-100 text-gray-400 cursor-not-allowed'
    }`;

  if (!providers) {
    return null;
  }

  // APP STORE COMPLIANCE: Hide ALL social login buttons on native iOS.
  // Apple Guideline 4.8 requires Sign in with Apple if other social logins are shown,
  // and a broken "Sign in with Apple" button caused Rejection #9 (Guideline 2.1a).
  // Email/password login is fully functional on the Auth page, so this is safe.
  const isNativeIOS =
    window.Capacitor?.isNativePlatform?.() && window.Capacitor?.getPlatform?.() === 'ios';
  if (isNativeIOS) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Apple - Required by App Store */}
      <button
        onClick={handleAppleLogin}
        disabled={loading.apple}
        className="w-full py-3 px-4 rounded-xl font-semibold text-sm flex items-center justify-center gap-3 transition-all bg-black hover:bg-gray-900 text-white border-2 border-black hover:shadow-md"
        data-testid="apple-login-btn"
      >
        {loading.apple ? (
          <div className="w-5 h-5 border-2 border-gray-500 border-t-white rounded-full animate-spin" />
        ) : (
          <AppleIcon />
        )}
        <span>Continue with Apple</span>
      </button>

      {/* Google - Always enabled */}
      <button
        onClick={handleGoogleLogin}
        disabled={loading.google}
        className={buttonClass(true)}
        data-testid="google-login-btn"
      >
        {loading.google ? (
          <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
        ) : (
          <GoogleIcon />
        )}
        <span>Continue with Google</span>
      </button>

      {/* Facebook */}
      <button
        onClick={handleFacebookLogin}
        disabled={!providers.facebook?.enabled || loading.facebook}
        className={buttonClass(providers.facebook?.enabled)}
        data-testid="facebook-login-btn"
      >
        {loading.facebook ? (
          <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
        ) : (
          <FacebookIcon />
        )}
        <span>Continue with Facebook</span>
      </button>
    </div>
  );
};

export default SocialLoginButtons;
